package com.swetdot.swetgram.ui.profile

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.imePadding
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.selection.toggleable
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.DatePicker
import androidx.compose.material3.DatePickerDialog
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Switch
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.material3.rememberDatePickerState
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.semantics.Role
import androidx.compose.ui.semantics.clearAndSetSemantics
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.text.input.KeyboardCapitalization
import androidx.compose.ui.text.style.TextAlign
import com.swetdot.swetgram.ui.text.resolve
import com.swetdot.swetgram.ui.text.resolveOrNull
import com.swetdot.swetgram.domain.LocalDay
import com.swetdot.swetgram.domain.MyProfile
import com.swetdot.swetgram.protocol.Constraints
import com.swetdot.swetgram.ui.auth.FieldError
import com.swetdot.swetgram.ui.auth.fieldErrorText
import com.swetdot.swetgram.ui.auth.validateUsername
import com.swetdot.swetgram.ui.chat.render
import com.swetdot.swetgram.ui.design.Radius
import com.swetdot.swetgram.ui.design.ContentWidth
import com.swetdot.swetgram.ui.design.Sizes
import com.swetdot.swetgram.ui.design.Space
import com.swetdot.swetgram.ui.design.SwetAvatar
import com.swetdot.swetgram.ui.design.SwetEmptyState
import com.swetdot.swetgram.ui.design.SwetErrorBanner
import com.swetdot.swetgram.ui.design.SwetLoading
import com.swetdot.swetgram.ui.design.SwetPrimaryButton
import com.swetdot.swetgram.ui.design.SwetSecondaryButton
import com.swetdot.swetgram.ui.design.SwetTextField
import com.swetdot.swetgram.ui.design.SwetTopAppBar
import com.swetdot.swetgram.ui.design.swetPressable
import com.swetdot.swetgram.ui.text.UiText
import com.swetdot.swetgram.ui.text.count
import com.swetdot.swetgram.ui.text.text
import com.swetdot.swetgram.ui.theme.SwetgramThemeTokens
import kotlinx.datetime.LocalDate
import kotlinx.datetime.TimeZone
import kotlinx.datetime.atStartOfDayIn
import kotlinx.datetime.toLocalDateTime
import org.jetbrains.compose.resources.stringResource
import swetgram.shared.generated.resources.Res
import swetgram.shared.generated.resources.profile_edit
import swetgram.shared.generated.resources.profile_edit_bio_counter
import swetgram.shared.generated.resources.profile_edit_bio_label
import swetgram.shared.generated.resources.profile_edit_bio_placeholder
import swetgram.shared.generated.resources.profile_edit_bio_too_long
import swetgram.shared.generated.resources.profile_edit_change_photo
import swetgram.shared.generated.resources.profile_edit_change_photo_action
import swetgram.shared.generated.resources.profile_edit_date_of_birth_label
import swetgram.shared.generated.resources.profile_edit_date_of_birth_placeholder
import swetgram.shared.generated.resources.profile_edit_date_of_birth_invalid
import swetgram.shared.generated.resources.profile_edit_date_pick
import swetgram.shared.generated.resources.profile_edit_date_clear
import swetgram.shared.generated.resources.profile_edit_date_confirm
import swetgram.shared.generated.resources.profile_edit_date_cancel
import swetgram.shared.generated.resources.profile_edit_discard_body
import swetgram.shared.generated.resources.profile_edit_discard_cancel
import swetgram.shared.generated.resources.profile_edit_discard_confirm
import swetgram.shared.generated.resources.profile_edit_discard_title
import swetgram.shared.generated.resources.profile_edit_display_name_label
import swetgram.shared.generated.resources.profile_edit_display_name_placeholder
import swetgram.shared.generated.resources.profile_edit_no_version
import swetgram.shared.generated.resources.profile_edit_location_auto
import swetgram.shared.generated.resources.profile_edit_location_auto_pending
import swetgram.shared.generated.resources.profile_edit_location_label
import swetgram.shared.generated.resources.profile_edit_location_placeholder
import swetgram.shared.generated.resources.profile_edit_location_too_long
import swetgram.shared.generated.resources.profile_edit_save
import swetgram.shared.generated.resources.profile_edit_show_birth_year
import swetgram.shared.generated.resources.profile_edit_uploading
import swetgram.shared.generated.resources.profile_edit_username_available_on
import swetgram.shared.generated.resources.profile_edit_username_label
import swetgram.shared.generated.resources.profile_edit_username_locked
import swetgram.shared.generated.resources.profile_edit_username_placeholder
import swetgram.shared.generated.resources.profile_try_again
import swetgram.shared.generated.resources.profile_unavailable_body_mine
import swetgram.shared.generated.resources.profile_unavailable_title
import kotlin.time.Clock
import kotlin.time.Instant

/**
 * Edit Profile -- the writable half of SPS §13.1.
 *
 * Three text fields -- display name, username and bio -- plus the avatar, which is the contract's
 * fourth writable property.
 *
 * The avatar does NOT go through this form's Save. It is uploaded and written the moment it is
 * picked, because `updateMe` is version-pinned: carrying a new media id until Save would make the
 * photo one more thing a `409 version_conflict` could discard, after the user had already waited
 * for the upload. See [EditProfileViewModel.changeAvatar].
 *
 * The screen never sends a field the user did not touch. That is not tidiness -- `updateMe` takes
 * a partial body, so a form that posted all three would become a read-modify-write and would
 * silently undo a change made on another device between this screen loading and Save being
 * tapped. [EditProfileViewModel.save] is where the diff is taken.
 *
 * The form seeds itself from the loaded profile ONCE, keyed by user id. A reload triggered by a
 * `409 version_conflict` therefore refreshes the version the save is pinned to without throwing
 * away what the user typed -- which is the entire point of reloading rather than just failing.
 */
@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun EditProfileScreen(
    state: EditProfileUiState,
    onSave: (
        displayName: String,
        username: String,
        bio: String,
        dateOfBirth: String,
        showBirthYear: Boolean,
        location: String,
        useLocationHint: Boolean,
    ) -> Unit,
    onBack: () -> Unit,
    modifier: Modifier = Modifier,
    /**
     * Called once the save lands. The caller navigates back AND calls
     * [EditProfileViewModel.consumeSaved], or the flag stays set and fires again on the next
     * composition.
     */
    onSaved: () -> Unit = onBack,
    onRetry: (() -> Unit)? = null,
    /**
     * Picks and saves a new avatar. No-op by default; the control is drawn only when
     * [EditProfileUiState.canEditAvatar] is also true, so a build with no chooser shows none.
     */
    onChangeAvatar: () -> Unit = {},
    now: Instant = Clock.System.now(),
    zone: TimeZone = TimeZone.currentSystemDefault(),
) {
    val colors = SwetgramThemeTokens.colors
    val profile = state.profile

    // Seeded once per account. `remember(key)` rather than a LaunchedEffect so the fields are
    // populated on the very frame the profile arrives, with no flash of empty boxes.
    var form by remember(profile?.user?.id) {
        mutableStateOf(
            EditProfileForm(
                displayName = profile?.user?.displayName.orEmpty(),
                username = profile?.user?.username.orEmpty(),
                bio = profile?.user?.bio.orEmpty(),
                dateOfBirth = profile?.user?.dateOfBirth.orEmpty(),
                showBirthYear = profile?.user?.showBirthYear ?: true,
                location = profile?.user?.location.orEmpty(),
            ),
        )
    }
    var submitted by remember { mutableStateOf(false) }
    var confirmingDiscard by remember { mutableStateOf(false) }
    var choosingDate by remember { mutableStateOf(false) }

    val usernameEditable = profile?.canChangeUsername(now) ?: false
    val errors = validate(
        form = form,
        profile = profile,
        usernameEditable = usernameEditable,
        today = now.toLocalDateTime(zone).date,
    )
    val dirty = profile != null && form.differsFrom(profile)

    LaunchedEffect(state.saved) {
        if (state.saved) onSaved()
    }

    fun requestBack() {
        if (dirty && !state.saving) confirmingDiscard = true else onBack()
    }

    Column(modifier.fillMaxSize()) {
        SwetTopAppBar(title = stringResource(Res.string.profile_edit), onBack = { requestBack() })

        // Over the form, never in place of it: a rejected save must leave what the user typed
        // exactly where it is.
        if (state.banner != null) {
            SwetErrorBanner(message = state.banner.resolve())
        }

        when {
            profile == null && !state.loadedOnce -> SwetLoading()

            profile == null -> SwetEmptyState(
                title = stringResource(Res.string.profile_unavailable_title),
                body = stringResource(Res.string.profile_unavailable_body_mine),
                actionText = if (onRetry != null) stringResource(Res.string.profile_try_again) else null,
                onAction = onRetry,
            )

            else -> Column(
                modifier = Modifier
                    .align(Alignment.CenterHorizontally)
                    .widthIn(max = ContentWidth.form)
                    .fillMaxSize()
                    // `imePadding` already lifts the form over the keyboard; this is the OTHER
                    // bottom inset, which applies whenever the keyboard is down. A pushed detail
                    // route has no tab bar under it to carry it, so without this the Save button
                    // sits on the system buttons. Both go before `verticalScroll` so they inset
                    // the viewport rather than travelling with the content.
                    .imePadding()
                    .navigationBarsPadding()
                    .verticalScroll(rememberScrollState())
                    .padding(horizontal = Space.screenMargin, vertical = Space.three),
            ) {
                if (state.canEditAvatar) {
                    AvatarField(
                        displayName = profile.user.displayName,
                        avatarUrl = profile.user.avatarUrl,
                        uploading = state.avatarUploading,
                        // Not during a save: both write the same version-pinned resource, and
                        // letting them race means one of the two loses with a 409.
                        enabled = !state.saving,
                        onChange = onChangeAvatar,
                    )
                    Spacer(Modifier.height(Space.three))
                }

                // Every field carries a LABEL as well as a placeholder. The placeholder alone was
                // not enough on this form and could not be: it is seeded with the account's
                // current details, so the placeholder is hidden from the first frame and two of
                // the three boxes were anonymous rounded rectangles containing a name and a
                // handle. SDS §11.2 asks for the label above the field for exactly this reason.
                SwetTextField(
                    value = form.displayName,
                    onValueChange = { form = form.copy(displayName = it) },
                    label = stringResource(Res.string.profile_edit_display_name_label),
                    placeholder = stringResource(Res.string.profile_edit_display_name_placeholder),
                    enabled = !state.saving,
                    keyboardOptions = KeyboardOptions(
                        capitalization = KeyboardCapitalization.Words,
                        imeAction = ImeAction.Next,
                    ),
                    errorText = fieldErrorText(
                        state.fieldErrors[FIELD_DISPLAY_NAME]
                            ?: errors.displayName.takeIf { submitted },
                    ).resolveOrNull(),
                )

                Spacer(Modifier.height(Space.two))

                SwetTextField(
                    value = form.username,
                    onValueChange = { form = form.copy(username = it) },
                    label = stringResource(Res.string.profile_edit_username_label),
                    // A RESOURCE WHOSE VALUE IS THE SAME IN EVERY LANGUAGE, on purpose. The
                    // contract restricts a username to a-z, 0-9 and underscore, so a Cyrillic
                    // example would demonstrate something the server refuses. It is DECLARED in
                    // `values-ru` all the same, with the same Latin value, rather than left to
                    // fall back: an identical value that somebody chose reads as a decision, and
                    // a missing one reads as an oversight.
                    placeholder = stringResource(Res.string.profile_edit_username_placeholder),
                    // SPS §8.2 rate-limits username changes, and the contract reports when the
                    // next one is allowed. Disabling the box is kinder than accepting a change
                    // the server will refuse.
                    enabled = usernameEditable && !state.saving,
                    keyboardOptions = KeyboardOptions(
                        // Usernames are lowercase-only in the contract; autocapitalising here
                        // would have the keyboard fight the rule.
                        capitalization = KeyboardCapitalization.None,
                        imeAction = ImeAction.Next,
                    ),
                    errorText = fieldErrorText(
                        state.fieldErrors[FIELD_USERNAME]
                            ?: errors.username.takeIf { submitted },
                    ).resolveOrNull(),
                )

                if (!usernameEditable) {
                    FieldNote(
                        // Two keys, because they are two claims: one names the day the lock lifts,
                        // the other admits it does not know. The date is an argument.
                        text = profile.usernameChangeAvailableAt
                            ?.let {
                                stringResource(
                                    Res.string.profile_edit_username_available_on,
                                    LocalDay.of(it, zone).render(),
                                )
                            }
                            ?: stringResource(Res.string.profile_edit_username_locked),
                    )
                }

                Spacer(Modifier.height(Space.two))

                SwetTextField(
                    value = form.bio,
                    onValueChange = { form = form.copy(bio = it) },
                    label = stringResource(Res.string.profile_edit_bio_label),
                    placeholder = stringResource(Res.string.profile_edit_bio_placeholder),
                    enabled = !state.saving,
                    // The one multi-line field on the form: a bio is prose, and a single line
                    // would hide most of the 160 characters the contract allows.
                    singleLine = false,
                    keyboardOptions = KeyboardOptions(
                        capitalization = KeyboardCapitalization.Sentences,
                        imeAction = ImeAction.Default,
                    ),
                    // The MESSAGE is chosen first and resolved once, rather than resolving each
                    // half and choosing between two Strings: both sides are a `UiText` now, and a
                    // server verdict still wins over the local check.
                    errorText = (
                        state.fieldErrors[FIELD_BIO]?.let { fieldErrorText(it) }
                            ?: errors.bio?.takeIf { submitted }
                        ).resolveOrNull(),
                )

                // Counter rather than a hard cap on input: silently swallowing the tail of a
                // pasted bio looks like a broken keyboard. The limit is stated and enforced on
                // Save instead.
                Text(
                    // A COUNTER, not a sentence: two numbers and a solidus. It is a resource all
                    // the same, so that a language which wants a different separator has somewhere
                    // to say so instead of needing this file changed.
                    text = stringResource(
                        Res.string.profile_edit_bio_counter,
                        form.bio.trim().length,
                        Constraints.BIO_MAX,
                    ),
                    style = MaterialTheme.typography.labelSmall,
                    color = if (form.bio.trim().length > Constraints.BIO_MAX) {
                        colors.danger
                    } else {
                        colors.textSecondary
                    },
                    textAlign = TextAlign.End,
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(horizontal = Space.one),
                )

                Spacer(Modifier.height(Space.two))

                SwetTextField(
                    value = form.dateOfBirth,
                    onValueChange = { form = form.copy(dateOfBirth = it) },
                    label = stringResource(Res.string.profile_edit_date_of_birth_label),
                    placeholder = stringResource(Res.string.profile_edit_date_of_birth_placeholder),
                    enabled = !state.saving,
                    keyboardOptions = KeyboardOptions(imeAction = ImeAction.Next),
                    errorText = errors.dateOfBirth?.takeIf { submitted }?.resolveOrNull(),
                )

                Spacer(Modifier.height(Space.one))
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(Space.two),
                ) {
                    SwetSecondaryButton(
                        text = stringResource(Res.string.profile_edit_date_pick),
                        onClick = { choosingDate = true },
                        enabled = !state.saving,
                        modifier = Modifier.weight(1f),
                    )
                    SwetSecondaryButton(
                        text = stringResource(Res.string.profile_edit_date_clear),
                        onClick = { form = form.copy(dateOfBirth = "") },
                        enabled = !state.saving && form.dateOfBirth.isNotBlank(),
                        modifier = Modifier.weight(1f),
                    )
                }

                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .toggleable(
                            value = form.showBirthYear,
                            enabled = !state.saving,
                            role = Role.Switch,
                            onValueChange = { form = form.copy(showBirthYear = it) },
                        )
                        .padding(vertical = Space.two),
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(Space.two),
                ) {
                    Text(
                        text = stringResource(Res.string.profile_edit_show_birth_year),
                        style = MaterialTheme.typography.bodyMedium,
                        color = colors.textPrimary,
                        modifier = Modifier.weight(1f),
                    )
                    Switch(checked = form.showBirthYear, onCheckedChange = null)
                }

                SwetTextField(
                    value = form.location,
                    onValueChange = {
                        form = form.copy(location = it, useLocationHint = false)
                    },
                    label = stringResource(Res.string.profile_edit_location_label),
                    placeholder = stringResource(Res.string.profile_edit_location_placeholder),
                    enabled = !state.saving,
                    keyboardOptions = KeyboardOptions(
                        capitalization = KeyboardCapitalization.Words,
                        imeAction = ImeAction.Done,
                    ),
                    errorText = errors.location?.takeIf { submitted }?.resolveOrNull(),
                )

                Spacer(Modifier.height(Space.one))
                SwetSecondaryButton(
                    text = stringResource(Res.string.profile_edit_location_auto),
                    onClick = { form = form.copy(location = "", useLocationHint = true) },
                    enabled = !state.saving,
                )
                if (form.useLocationHint) {
                    FieldNote(text = stringResource(Res.string.profile_edit_location_auto_pending))
                }

                Spacer(Modifier.height(Space.four))

                SwetPrimaryButton(
                    text = stringResource(Res.string.profile_edit_save),
                    onClick = {
                        submitted = true
                        if (errors.isValid) {
                            onSave(
                                form.displayName,
                                form.username,
                                form.bio,
                                form.dateOfBirth,
                                form.showBirthYear,
                                form.location,
                                form.useLocationHint,
                            )
                        }
                    },
                    // False when the server sent no `version`. Saving without one would be a
                    // write with nothing to check it against, which the contract's optimistic
                    // concurrency exists to prevent.
                    enabled = state.canSave,
                    loading = state.saving,
                )

                if (profile.version == null) {
                    FieldNote(text = stringResource(Res.string.profile_edit_no_version))
                }

                Spacer(Modifier.height(Space.four))
            }
        }
    }

    if (confirmingDiscard) {
        // Discarding typed text is not recoverable, so it gets §11.1's confirmation like any
        // other destructive action.
        ProfileConfirmDialog(
            title = stringResource(Res.string.profile_edit_discard_title),
            body = stringResource(Res.string.profile_edit_discard_body),
            confirmText = stringResource(Res.string.profile_edit_discard_confirm),
            onConfirm = {
                confirmingDiscard = false
                onBack()
            },
            onDismiss = { confirmingDiscard = false },
            // The one dialog in the app that overrides `ProfileConfirmDialog`'s defaulted Cancel:
            // "Keep editing" says what the safe button DOES, which matters more here than
            // elsewhere because both buttons lead back to a screen.
            cancelText = stringResource(Res.string.profile_edit_discard_cancel),
        )
    }

    if (choosingDate) {
        val pickerState = rememberDatePickerState(
            initialSelectedDateMillis = form.dateOfBirth.toDateOrNull()
                ?.atStartOfDayIn(TimeZone.UTC)
                ?.toEpochMilliseconds(),
        )
        DatePickerDialog(
            onDismissRequest = { choosingDate = false },
            confirmButton = {
                TextButton(
                    onClick = {
                        pickerState.selectedDateMillis?.let { millis ->
                            form = form.copy(
                                dateOfBirth = Instant.fromEpochMilliseconds(millis)
                                    .toLocalDateTime(TimeZone.UTC)
                                    .date
                                    .toString(),
                            )
                        }
                        choosingDate = false
                    },
                ) { Text(stringResource(Res.string.profile_edit_date_confirm)) }
            },
            dismissButton = {
                TextButton(onClick = { choosingDate = false }) {
                    Text(stringResource(Res.string.profile_edit_date_cancel))
                }
            },
        ) {
            DatePicker(state = pickerState)
        }
    }
}

/**
 * The avatar, and the action that replaces it.
 *
 * ONE tap target covering the picture and its label, rather than a picture that happens to be
 * tappable next to a separate button: the whole row is the control, so it clears §22.1's minimum
 * comfortably and a screen reader reaches one node that says what it does.
 *
 * The circle shows the STORED photo, falling back to initials while it loads and if it cannot be
 * fetched. [avatarUrl] comes from the reloaded profile rather than from the bytes just picked, so
 * what is on screen after an upload is what the server will serve to everybody else -- an optimistic
 * local preview would show a picture that a failed `updateMyProfile` had never actually saved.
 */
@Composable
private fun AvatarField(
    displayName: String,
    avatarUrl: String?,
    uploading: Boolean,
    enabled: Boolean,
    onChange: () -> Unit,
) {
    val colors = SwetgramThemeTokens.colors
    val label = if (uploading) {
        stringResource(Res.string.profile_edit_uploading)
    } else {
        stringResource(Res.string.profile_edit_change_photo)
    }
    // The action a screen reader announces for the row. A verb phrase rather than the label above,
    // which is a noun once the upload starts.
    val clickLabel = stringResource(Res.string.profile_edit_change_photo_action)

    Row(
        modifier = Modifier
            .fillMaxWidth()
            // Clipped BEFORE the press feedback, which SwetMotion requires: the state layer is a
            // plain rectangle and would otherwise paint over these corners.
            .clip(RoundedCornerShape(Radius.lg))
            .swetPressable(
                onClick = onChange,
                enabled = enabled && !uploading,
                role = Role.Button,
                onClickLabel = clickLabel,
            )
            .padding(Space.one),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(Space.two),
    ) {
        // Semantics cleared: the initials would otherwise be announced as a second, meaningless
        // node inside a control that already says what it is.
        SwetAvatar(
            displayName = displayName,
            size = Sizes.avatarLarge,
            modifier = Modifier.clearAndSetSemantics { },
            avatarUrl = avatarUrl,
        )
        Text(
            text = label,
            style = MaterialTheme.typography.bodyMedium,
            // Primary text, because this is the row's action and the accent is a FILL colour: it
            // is dark enough to carry a white label on itself, which is exactly what makes it
            // illegible as small text on a card. Never textTertiary either -- that token is for
            // placeholder and disabled text only.
            color = if (enabled && !uploading) colors.textPrimary else colors.textSecondary,
        )
    }
}

/** A quiet line under a field: a rule, a limit, or why something is unavailable. */
@Composable
private fun FieldNote(text: String) {
    Text(
        text = text,
        style = MaterialTheme.typography.labelMedium,
        color = SwetgramThemeTokens.colors.textSecondary,
        modifier = Modifier
            .fillMaxWidth()
            .padding(horizontal = Space.one, vertical = Space.half),
    )
}

// ---------------------------------------------------------------------------------------------
// Form state and validation
// ---------------------------------------------------------------------------------------------

/** The three editable fields, held as typed by the user -- trimming happens at the boundary. */
internal data class EditProfileForm(
    val displayName: String,
    val username: String,
    val bio: String,
    val dateOfBirth: String = "",
    val showBirthYear: Boolean = true,
    val location: String = "",
    val useLocationHint: Boolean = false,
) {
    /**
     * Compared on the TRIMMED values, so adding and removing a trailing space does not arm the
     * discard dialog -- and does not send an edit that changes nothing.
     */
    fun differsFrom(profile: MyProfile): Boolean =
        displayName.trim() != profile.user.displayName ||
            username.trim().lowercase() != profile.user.username ||
            bio.trim() != profile.user.bio.orEmpty().trim() ||
            dateOfBirth.trim() != profile.user.dateOfBirth.orEmpty() ||
            showBirthYear != profile.user.showBirthYear ||
            location.trim() != profile.user.location.orEmpty().trim() ||
            useLocationHint
}

internal data class EditProfileErrors(
    val displayName: FieldError? = null,
    val username: FieldError? = null,
    /**
     * A [UiText] and not a `FieldError`: that enum has no bio member, and adding one is
     * `ui/auth`'s call rather than this file's. It used to be a plain `String`, which is the shape
     * that made the sentence untranslatable -- the rule chose the words, so a Russian phone got
     * English under the bio box and nothing anywhere could see it.
     */
    val bio: UiText? = null,
    val dateOfBirth: UiText? = null,
    val location: UiText? = null,
) {
    val isValid: Boolean get() =
        displayName == null && username == null && bio == null &&
            dateOfBirth == null && location == null
}

/**
 * Client-side validation, mirroring the contract's own `Constraints` so the two cannot drift.
 *
 * It does not replace the server's checks -- the server still decides -- it just spends a round
 * trip only when there is a real chance of success.
 *
 * The username is validated ONLY when it changed. An account created under an older rule set could
 * hold a name this build would now reject, and refusing to let that user edit their bio because of
 * a username they are not touching would be a bug with no way out of it.
 */
internal fun validate(
    form: EditProfileForm,
    profile: MyProfile?,
    usernameEditable: Boolean,
    today: LocalDate = Clock.System.now().toLocalDateTime(TimeZone.UTC).date,
): EditProfileErrors {
    val name = form.displayName.trim()
    val bio = form.bio.trim()
    val usernameChanged =
        profile != null && form.username.trim().lowercase() != profile.user.username

    return EditProfileErrors(
        displayName = when {
            name.isEmpty() -> FieldError.Required
            name.length > Constraints.DISPLAY_NAME_MAX -> FieldError.DisplayNameTooLong
            else -> null
        },
        username = if (usernameChanged && usernameEditable) validateUsername(form.username) else null,
        // A PLURAL OVER A CONSTANT, which looks like over-engineering and is not. The bound is
        // `Constraints.BIO_MAX` and it comes from the contract; it is 160 today, and a later
        // contract making it 21 would need Russian's "символа" with nothing in Kotlin able to know.
        // Declaring the plural now costs one call and removes the trap entirely -- and the form is
        // chosen by CLDR from the count, never by an `if` here.
        bio = if (bio.length > Constraints.BIO_MAX) {
            Res.plurals.profile_edit_bio_too_long.count(Constraints.BIO_MAX)
        } else {
            null
        },
        dateOfBirth = form.dateOfBirth.trim().takeIf { it.isNotEmpty() }?.let { raw ->
            val parsed = raw.toDateOrNull()
            if (parsed == null || parsed > today) {
                Res.string.profile_edit_date_of_birth_invalid.text()
            } else {
                null
            }
        },
        location = if (form.location.trim().length > Constraints.LOCATION_MAX) {
            Res.string.profile_edit_location_too_long.text()
        } else {
            null
        },
    )
}

private fun String.toDateOrNull(): LocalDate? =
    runCatching { LocalDate.parse(trim()) }.getOrNull()
